import {
  fileCopiedOnly,
  fileModeChangedOnlyNoOtherChanges,
  fileRenamedOnly,
  fileTruncated,
  fileWasDeleted,
  fileWasGenerated,
  truncatedReason,
  whitespaceChangedOnly,
} from '@github-ui/diff-file-helpers'
import {SplitDiffTable, UnifiedDiffTable} from '@github-ui/diffs/DiffParts'
import {getLineNumberWidth} from '@github-ui/diffs/diff-line-helpers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import {Link} from '@primer/react'
import type React from 'react'
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import HiddenDiffPatch from './HiddenDiffPatch'
import {
  type FileDiffReference,
  type DiffEntryData,
  type DiffLine,
  isDiffOrientation,
  type DiffOrientation,
} from '../types'
import styles from './DiffLines.module.css'
import {clsx} from 'clsx'
import {HiddenUnicodeBanner} from '@github-ui/hidden-unicode-banner/HiddenUnicodeBanner'
import {hasHiddenUnicodeCharacters} from '@github-ui/hidden-unicode-banner/utils'
import {
  useDiffLinesActions,
  useGetFirstLineSelectionOnDrag,
  useGetSelectedDiffLines,
  useGetSelectedLineRange,
  useHasSelectedLines,
} from '../stores/selectors'
import {ariaAnnounceClearedSelection} from '../helpers/announce-selected-line-changes'
import {HunkHeaderData} from '../helpers/hunk-data-helpers'
import type {ViewerData} from '../conversation-types'
import {SimpleUnifiedDiffLines} from './simple-diff/SimpleUnifiedDiffLines'
import {SimpleSplitDiffLines} from './simple-diff/SimpleSplitDiffLines'
import {handleLineSelectionEffects} from '../helpers/line-selection'
import {isDiffLineKey} from '../stores/slices/helpers/diff-lines-slice-helpers'
import {useIsSelectedFile, useUpdateSelectedPathDigest} from '@github-ui/pull-requests/pull-request-store-selectors'
import {getGlobalINPObserver} from '@github-ui/web-vitals'
import {sendCustomMetric} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {copyCode} from '../helpers/copy-code'
import {copyText} from '@github-ui/copy-to-clipboard'
import {useGetINPDiffTags} from '@github-ui/pull-requests/pull-requests-analytics-store-selectors'

function PlainTextStatus({diffAnchor, children}: React.PropsWithChildren<{diffAnchor: string}>) {
  return (
    <div className="fgColor-muted p-2" data-diff-anchor={diffAnchor}>
      {children}
    </div>
  )
}

export interface DiffLinesProps {
  hasExpandedAllRanges?: boolean
  baseHelpUrl: string
  diffEntryData: DiffEntryData
  diffLinesManuallyUnhidden: boolean
  handleSetShowHiddenUnicodeCharacters: (val: boolean) => void
  onHandleLoadDiff: () => void
  copilotChatReference?: FileDiffReference
  isSplitView: boolean
  showHiddenUnicodeCharacters: boolean
  subjectId: string
  tableAriaLabel: string | undefined
  viewerData: ViewerData
}

export const DiffLines = memo(function DiffLines({
  diffEntryData,
  diffLinesManuallyUnhidden,
  baseHelpUrl,
  handleSetShowHiddenUnicodeCharacters,
  onHandleLoadDiff,
  copilotChatReference,
  hasExpandedAllRanges,
  isSplitView,
  showHiddenUnicodeCharacters,
  subjectId,
  tableAriaLabel,
  viewerData,
}: DiffLinesProps) {
  const diffAnchor: DiffAnchor = `diff-${diffEntryData.pathDigest}`

  if (diffEntryData.isBinary) return <PlainTextStatus diffAnchor={diffAnchor}>Binary file not shown.</PlainTextStatus>
  if (fileRenamedOnly(diffEntryData))
    return <PlainTextStatus diffAnchor={diffAnchor}>File renamed without changes.</PlainTextStatus>
  if (fileCopiedOnly(diffEntryData))
    return <PlainTextStatus diffAnchor={diffAnchor}>File copied without changes.</PlainTextStatus>
  if (
    fileModeChangedOnlyNoOtherChanges(
      diffEntryData,
      diffEntryData.status,
      diffEntryData.oldTreeEntry?.mode,
      diffEntryData.newTreeEntry?.mode,
    )
  )
    return <PlainTextStatus diffAnchor={diffAnchor}>File mode changed.</PlainTextStatus>
  if (!diffLinesManuallyUnhidden && fileTruncated(diffEntryData))
    // Safe, because we just validated that field exists in the predicate above.
    return (
      <HiddenDiffPatch diffAnchor={diffAnchor} onLoadDiff={onHandleLoadDiff}>
        {truncatedReason(diffEntryData.truncatedReason as string)}
      </HiddenDiffPatch>
    )
  if (whitespaceChangedOnly(diffEntryData))
    return <PlainTextStatus diffAnchor={diffAnchor}>Whitespace-only changes.</PlainTextStatus>
  if (!diffLinesManuallyUnhidden && fileWasDeleted(diffEntryData))
    return (
      <HiddenDiffPatch diffAnchor={diffAnchor} onLoadDiff={onHandleLoadDiff}>
        This file was deleted.
      </HiddenDiffPatch>
    )
  if (!diffLinesManuallyUnhidden && fileWasGenerated(diffEntryData)) {
    return (
      <HiddenDiffPatch
        diffAnchor={diffAnchor}
        helpText="customizing how changed files appear on GitHub."
        helpUrl={`${baseHelpUrl}/github/administering-a-repository/customizing-how-changed-files-appear-on-github`}
        onLoadDiff={onHandleLoadDiff}
      >
        Some generated files are not rendered by default. Learn more about{' '}
      </HiddenDiffPatch>
    )
  }

  if (diffEntryData.isTooBig && diffEntryData.diffLines.length === 0) {
    if (diffLinesManuallyUnhidden) {
      return (
        <PlainTextStatus diffAnchor={diffAnchor}>
          Diff is too big to render. To view,{' '}
          <Link
            inline
            href={`${baseHelpUrl}/pull-requests/collaborating-with-pull-requests/reviewing-changes-in-pull-requests/checking-out-pull-requests-locally`}
          >
            check out this pull request locally.
          </Link>
        </PlainTextStatus>
      )
    } else {
      return (
        <HiddenDiffPatch diffAnchor={diffAnchor} onLoadDiff={onHandleLoadDiff}>
          Large diffs are not rendered by default.
        </HiddenDiffPatch>
      )
    }
  }

  if (!diffEntryData.diffLines) return null

  return (
    <CodeDiffLines
      diffAnchor={diffAnchor}
      diffEntryData={diffEntryData}
      viewerData={viewerData}
      copilotChatReference={copilotChatReference}
      handleSetShowHiddenUnicodeCharacters={handleSetShowHiddenUnicodeCharacters}
      hasExpandedAllRanges={hasExpandedAllRanges}
      isSplitView={isSplitView}
      showHiddenUnicodeCharacters={showHiddenUnicodeCharacters}
      subjectId={subjectId}
      tableAriaLabel={tableAriaLabel}
    />
  )
})

type CodeDiffLinesProps = Pick<
  DiffLinesProps,
  | 'diffEntryData'
  | 'viewerData'
  | 'copilotChatReference'
  | 'handleSetShowHiddenUnicodeCharacters'
  | 'hasExpandedAllRanges'
  | 'isSplitView'
  | 'showHiddenUnicodeCharacters'
  | 'subjectId'
  | 'tableAriaLabel'
> & {
  diffAnchor: DiffAnchor
}

function CodeDiffLines({
  diffAnchor,
  viewerData,
  diffEntryData,
  copilotChatReference,
  handleSetShowHiddenUnicodeCharacters,
  hasExpandedAllRanges,
  isSplitView,
  showHiddenUnicodeCharacters,
  tableAriaLabel,
}: CodeDiffLinesProps) {
  const tableRef = useRef<HTMLTableElement>(null)
  const {clearSelectedLines, clearFirstLineSelectionOnDrag} = useDiffLinesActions()
  const getFirstLineSelectionOnDrag = useGetFirstLineSelectionOnDrag()
  const [diffSideBeingBlocked, setDiffSideBeingBlocked] = useState<'left' | 'right' | null>(null)
  const fileLineCount = diffEntryData.newTreeEntry?.lineCount ?? diffEntryData.oldTreeEntry?.lineCount ?? 0

  const diffLines: DiffLine[] = useMemo(() => {
    const lastDiffLine = diffEntryData.diffLines[diffEntryData.diffLines.length - 1]

    // We need to append a client only hunk diffline type to allow for expanding the file
    // until EOF has been returned in diffEntryData.diffLines
    if (lastDiffLine?.blobLineNumber && lastDiffLine.blobLineNumber < fileLineCount) {
      return diffEntryData.diffLines.concat({
        __id: HunkHeaderData.finalLineId,
        blobLineNumber: lastDiffLine.blobLineNumber + 1,
        left: null,
        right: null,
        type: 'HUNK',
        html: '',
        text: '',
      } as DiffLine)
    }

    return diffEntryData.diffLines
  }, [diffEntryData.diffLines, fileLineCount])

  const {lineSpacingPreference, tabSizePreference} = viewerData

  const hasSelectedLines = useHasSelectedLines()
  const isSelectedFile = useIsSelectedFile(diffEntryData.pathDigest)

  // This effect handles clearing out line selection when a user has selected a different diff
  useEffect(() => {
    if (!isSelectedFile && hasSelectedLines()) {
      clearSelectedLines()
      ariaAnnounceClearedSelection()
    }
  }, [clearSelectedLines, hasSelectedLines, isSelectedFile])

  const lineNumberWidth = getLineNumberWidth(diffLines)

  const handleDiffSideCellSelectionBlockingOptimized = useCallback(
    (event: React.MouseEvent) => {
      const diffSideToBlock = event.currentTarget.classList.contains('left-side-diff-cell') ? 'right' : 'left'
      setDiffSideBeingBlocked(diffSideToBlock)
    },
    [setDiffSideBeingBlocked],
  )

  const expandAllHunks = useCallback(
    (e: React.KeyboardEvent<HTMLTableElement>) => {
      if (
        e.shiftKey &&
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        e.metaKey &&
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        e.key === 'ArrowLeft' &&
        !hasExpandedAllRanges &&
        (e.target as HTMLElement)?.getAttribute('role') === 'gridcell'
      ) {
        const expandAllHunksButton = document.querySelector(
          `.js-expand-all-difflines-button[data-file-path="${diffEntryData.path}"]`,
        )
        expandAllHunksButton?.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true}))
        // TODO: Manage keyboard focus
      }
    },
    [hasExpandedAllRanges, diffEntryData.path],
  )

  const getSelectedLineRange = useGetSelectedLineRange()
  const getSelectedDiffLines = useGetSelectedDiffLines()

  const handleCopyDiffLines = useCallback(
    async (event: React.ClipboardEvent<HTMLTableElement>) => {
      const selection = document.getSelection()

      // Handle cmd/ctrl + c copy for browser/mouse selection
      if (selection && selection.rangeCount >= 1) {
        const textOutput = []

        for (let i = 0; i < selection.rangeCount; i++) {
          const range = selection.getRangeAt(i)
          const content = range.cloneContents()

          // Check if the selection contains a 'td' element, if not it is a fragment of a line and we are free to copy the text content
          if (content.querySelectorAll('td').length) {
            // Remove line numbers, +/- markers, and the opposite side from the copy
            const unwantedElementsQuery = `td.diff-line-number, td.new-diff-line-number, span.diff-text-marker${
              isSplitView
                ? `, ${diffSideBeingBlocked === 'left' ? 'td.left-side-diff-cell' : 'td.right-side-diff-cell'}`
                : ''
            }`
            const unwantedElements = content.querySelectorAll(unwantedElementsQuery)
            for (const el of unwantedElements) {
              if (el.parentNode) {
                el.parentNode.removeChild(el)
              }
            }

            for (const child of content.children) {
              // In firefox, the range is split up into multiple elements which can include the markers and context menu
              // While the content itself is filtered out from the unwanted elements query above, it still iterates over
              // the node, adding empty strings to the text output which are joined with a new line. To prevent copying
              // those extra blank lines, we check if the child has or is a diff text cell as that's all we want to copy.
              if (child.querySelectorAll('td.diff-text-cell').length === 0 && !child.matches?.('td.diff-text-cell')) {
                continue
              }

              const text = (child as HTMLElement).textContent

              // Will be joined it a '\n`
              if (text === '\n') {
                textOutput.push('')
              } else {
                textOutput.push(text)
              }
            }
          } else {
            if (selection.rangeCount === 1) {
              const text = selection.toString()

              // If the selection has no text, we skip it
              if (!text) continue

              // If the selection is a single range, we can just copy the text of the selection
              // This avoids a weird issue with comment textareas technically not having textContent
              textOutput.push(text)
            } else {
              const text = content.textContent

              // If the content has no text, we skip it
              if (!text) continue

              textOutput.push(text)
            }
          }
        }

        event.clipboardData.setData('text/plain', textOutput.join('\n'))
        event.preventDefault()
        return
      }

      // Handle cmd/ctrl + c copy for line number selection

      // Only handle line selection copy if called on a TD element.
      if ((event.target as HTMLElement).nodeName !== 'TD') return

      const diffSide = (event.target as HTMLElement).getAttribute('data-diff-side')
      // Early return if there is no data-diff-side attribute on the TD element
      if (!diffSide) return

      const isLeftSide = diffSide === 'left'
      const firstDiffLine = getSelectedDiffLines()[0]

      if (!firstDiffLine) {
        await copyText((event.target as HTMLElement).textContent)
        return
      }

      await copyCode({
        diffLine: firstDiffLine,
        diffLines,
        getSelectedDiffLines,
        getSelectedLineRange,
        isSplit: isSplitView,
        isLeftSide,
      })
    },
    [diffLines, diffSideBeingBlocked, getSelectedDiffLines, getSelectedLineRange, isSplitView],
  )

  const diffHasHiddenUnicodeCharacters = useMemo(() => {
    return diffLines.some(line => hasHiddenUnicodeCharacters(line.text))
  }, [diffLines])

  const {addLineToSelection, selectLine} = useDiffLinesActions()
  const updateSelectedPathDigest = useUpdateSelectedPathDigest()
  const {sendAnalyticsEvent} = useAnalytics()
  const getINPDiffTags = useGetINPDiffTags()

  const handleMouseDownOnNumberCell = useCallback(
    (e: React.MouseEvent) => {
      const el = e.target as HTMLElement
      const lineKey = el.getAttribute('data-diff-line-key')
      const orientation = el.getAttribute('data-diff-side')

      // Early return if lineKey or orientation are not of type DiffLineKey or DiffOrientation
      if (!isDiffLineKey(lineKey) || !isDiffOrientation(orientation)) {
        return
      }

      // Clear mouse selection of text on line selection to ensure `cmd/ctrl + c` works for users
      const selection = document.getSelection()
      selection?.empty()

      const tags = getINPDiffTags()

      // Send DD metric when user clicks on a diff's line number
      getGlobalINPObserver()?.registerCallback({
        event: e.nativeEvent,
        cb: interaction => {
          sendAnalyticsEvent('diff-lines.inp_interaction', 'PULL_REQUESTS_DIFF_LINE_NUMBER_CLICK', {
            latency: interaction.latency,
          })
          sendCustomMetric({
            name: 'PULL_REQUESTS_DIFF_LINE_NUMBER_CLICK',
            tags,
            value: interaction.latency,
          })
        },
      })

      const selectedLines = getSelectedDiffLines()

      // Deselect lines when clicking on a single line that was already selected
      if (
        lineKey &&
        selectedLines.length === 1 &&
        selectedLines[0]?.key === lineKey &&
        getFirstLineSelectionOnDrag() === null
      ) {
        clearSelectedLines()
        updateSelectedPathDigest(undefined)
        return
      }

      const selectedLineRange = getSelectedLineRange()

      // We only handle adding lines to selection on shift + click for onMouseDown event handlers.
      if (selectedLineRange && e.shiftKey) {
        addLineToSelection(
          {
            lineKey,
            orientation,
          },
          handleLineSelectionEffects,
        )
        return
      }

      const lineNumberStr = el.getAttribute('data-line-number')

      if (lineNumberStr) {
        selectLine(
          {
            lineKey,
            firstLineSelectionOnDrag: {
              orientation,
              lineNumber: parseInt(lineNumberStr),
              shouldStartConversation: false,
              lineKey,
            },
          },
          handleLineSelectionEffects,
        )
        updateSelectedPathDigest(diffEntryData.pathDigest)
      }
    },
    [
      addLineToSelection,
      clearSelectedLines,
      diffEntryData.pathDigest,
      getFirstLineSelectionOnDrag,
      getINPDiffTags,
      getSelectedDiffLines,
      getSelectedLineRange,
      selectLine,
      sendAnalyticsEvent,
      updateSelectedPathDigest,
    ],
  )

  const handleMouseUpRow = useCallback(() => {
    // Only clear selection on state if there is defined FirstLineSelectionOnDrag
    if (getFirstLineSelectionOnDrag() !== null) {
      clearFirstLineSelectionOnDrag()
    }
  }, [clearFirstLineSelectionOnDrag, getFirstLineSelectionOnDrag])

  const handleMouseEnterCell = useCallback(
    (e: React.MouseEvent) => {
      if (e.buttons !== 1) return
      const el = e.target as HTMLElement
      const lineKey = el.closest('[data-diff-line-key]')?.getAttribute('data-diff-line-key')
      const orientation = el.closest('[data-diff-side]')?.getAttribute('data-diff-side')

      // Early return if lineKey or orientation are not of type DiffLineKey or DiffOrientation
      if (!isDiffLineKey(lineKey) || !isDiffOrientation(orientation)) {
        return
      }

      const firstLineSelectionOnDrag = getFirstLineSelectionOnDrag()

      if (firstLineSelectionOnDrag) {
        addLineToSelection(
          {
            lineKey,
            orientation,
          },
          handleLineSelectionEffects,
        )
      }
    },
    [getFirstLineSelectionOnDrag, addLineToSelection],
  )

  const isLastSelectedLine = useCallback(
    (orientation: DiffOrientation, lineNumber: number) => {
      const lineRange = getSelectedLineRange()

      if (!lineRange) {
        return false
      }

      const isLeftSide = orientation === 'left'

      const lineIsOnRightSide = lineRange.endOrientation === 'right' && !isLeftSide
      if (lineIsOnRightSide && lineRange.endLineNumber === lineNumber) {
        return true
      }

      const lineIsOnLeftSide = lineRange && lineRange.endOrientation === 'left' && isLeftSide
      if (lineIsOnLeftSide && lineRange.endLineNumber === lineNumber) {
        return true
      }

      return false
    },
    [getSelectedLineRange],
  )

  const handleMouseLeaveAddCommentButton = useCallback(
    (e: React.MouseEvent) => {
      // only handle line selection if mouse is being held down.
      if (e.buttons !== 1) return

      const el = e.target as HTMLElement
      const lineKey = el.closest('[data-diff-line-key]')?.getAttribute('data-diff-line-key')
      const orientation = el.closest('[data-diff-side]')?.getAttribute('data-diff-side')

      // Early return if lineKey or orientation are not of type DiffLineKey or DiffOrientation
      if (!isDiffLineKey(lineKey) || !isDiffOrientation(orientation)) {
        return
      }

      if (!lineKey) return
      const isRowSelected = el.closest('[data-selected]')?.getAttribute('data-selected')
      const dataLineNumber = el.closest('[data-line-number]')?.getAttribute('data-line-number')
      const lineNumber = dataLineNumber ? parseInt(dataLineNumber, 10) : undefined

      if (!isRowSelected && lineNumber && !isLastSelectedLine(orientation, lineNumber)) {
        selectLine(
          {
            lineKey,
            firstLineSelectionOnDrag: {
              lineNumber,
              orientation,
              lineKey,
              shouldStartConversation: true,
            },
          },
          handleLineSelectionEffects,
        )
      } else {
        addLineToSelection(
          {
            lineKey,
            orientation,
          },
          handleLineSelectionEffects,
        )
      }

      if (!isSelectedFile) {
        updateSelectedPathDigest(diffEntryData.pathDigest)
      }
    },
    [
      addLineToSelection,
      diffEntryData.pathDigest,
      isLastSelectedLine,
      isSelectedFile,
      selectLine,
      updateSelectedPathDigest,
    ],
  )

  const handleMouseDownAddCommentButton = useCallback(
    (e: React.MouseEvent) => {
      const el = e.target as HTMLElement
      const orientation = el.closest('[data-diff-side]')?.getAttribute('data-diff-side')
      const dataLineNumber = el.closest('[data-line-number]')?.getAttribute('data-line-number')
      const lineNumber = dataLineNumber ? parseInt(dataLineNumber, 10) : undefined
      // Clears line selection when user clicks on add comment button on any line but the last one selected.
      if (isDiffOrientation(orientation) && lineNumber && !isLastSelectedLine(orientation, lineNumber)) {
        clearSelectedLines()
      }
    },
    [clearSelectedLines, isLastSelectedLine],
  )

  return (
    <>
      {diffHasHiddenUnicodeCharacters && (
        <HiddenUnicodeBanner
          isShown={showHiddenUnicodeCharacters}
          toggleShowHiddenCharacters={() => handleSetShowHiddenUnicodeCharacters(!showHiddenUnicodeCharacters)}
        />
      )}
      <table
        aria-label={tableAriaLabel}
        onKeyDown={expandAllHunks}
        ref={tableRef}
        className={clsx('tab-size', 'width-full', styles.tableLayoutFixed, {
          [styles.compact]: lineSpacingPreference === 'compact',
        })}
        data-block-diff-cell-selection={diffSideBeingBlocked}
        data-diff-anchor={diffAnchor}
        data-tab-size={tabSizePreference}
        data-paste-markdown-skip
        onCopy={handleCopyDiffLines}
        role="grid"
        style={{
          '--line-number-cell-width': `${lineNumberWidth}px`,
          '--line-number-cell-width-unified': `${parseFloat(lineNumberWidth) * 2}px`,
        }}
      >
        {isSplitView ? (
          <SplitDiffTable lineWidth={lineNumberWidth}>
            <SimpleSplitDiffLines
              diffAnchor={diffAnchor}
              diffLines={diffLines}
              filePath={diffEntryData.path}
              handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlockingOptimized}
              handleMouseDownOnNumberCell={handleMouseDownOnNumberCell}
              handleMouseEnterCell={handleMouseEnterCell}
              handleMouseUpRow={handleMouseUpRow}
              handleMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
              handleMouseDownAddCommentButton={handleMouseDownAddCommentButton}
              copilotChatReference={copilotChatReference}
              // passing in the basic types rather than the full viewer Data object so that any modifications
              // to the viewer data object don't cause a rerender of the children unnecessarily
              avatarURL={viewerData.avatarUrl}
              userLogin={viewerData.login}
              viewerCanApplySuggestion={viewerData.viewerCanApplySuggestion}
              viewerCanComment={viewerData.viewerCanComment}
              shouldDisplayComments={viewerData.shouldDisplayComments}
              tableRef={tableRef}
            />
          </SplitDiffTable>
        ) : (
          <UnifiedDiffTable lineWidth={lineNumberWidth}>
            <SimpleUnifiedDiffLines
              diffAnchor={diffAnchor}
              diffLines={diffLines}
              filePath={diffEntryData.path}
              handleMouseDownOnNumberCell={handleMouseDownOnNumberCell}
              handleMouseEnterCell={handleMouseEnterCell}
              handleMouseUpRow={handleMouseUpRow}
              handleMouseLeaveAddCommentButton={handleMouseLeaveAddCommentButton}
              handleMouseDownAddCommentButton={handleMouseDownAddCommentButton}
              copilotChatReference={copilotChatReference}
              // passing in the basic types rather than the full viewer Data object so that any modifications
              // to the viewer data object don't cause a rerender of the children unnecessarily
              avatarURL={viewerData.avatarUrl}
              userLogin={viewerData.login}
              viewerCanApplySuggestion={viewerData.viewerCanApplySuggestion}
              viewerCanComment={viewerData.viewerCanComment}
              shouldDisplayComments={viewerData.shouldDisplayComments}
              tableRef={tableRef}
            />
          </UnifiedDiffTable>
        )}
      </table>
    </>
  )
}
